import { useState, useRef, useCallback, useEffect } from 'react';
// Rechtstexte liegen in einer eigenen Datei, weil dort die einzigen Angaben
// stehen, die der Betreiber ausfüllen muss (LEGAL-Block ganz oben) — so ist
// „ausrollen" ein Blick in EINE Datei statt eine Suche durch die Seite.
import { Privacy, Impressum, Support } from './LegalTexts';
import { useInstanceMode } from '../hooks/useInstanceMode';
import { serviceOrigin } from '../lib/services';
// Produktseiten-Grafiken (Figma, Post #4706 q-0d6cb6f3cddb, Storage 119520-26):
// currentColor-basiert, deshalb INLINE eingebettet statt <img> — nur im DOM
// folgt die Grafik der Textfarbe des Containers in hell UND dunkel.
import heroFieldSvg from '../assets/product/hero-field.svg?raw';
import iconIsolation from '../assets/product/icon-isolation.svg?raw';
import iconTeam from '../assets/product/icon-team.svg?raw';
import iconNachvollziehbar from '../assets/product/icon-nachvollziehbar.svg?raw';
import iconVerwaltet from '../assets/product/icon-verwaltet.svg?raw';
import iconPlattform from '../assets/product/icon-plattform.svg?raw';
import iconSkalierung from '../assets/product/icon-skalierung.svg?raw';

/** Inline-SVG (vertrauenswürdige Repo-Assets, beim Einchecken geprüft:
 *  keine Skripte/foreignObject). */
function Art({ markup, className }: { markup: string; className: string }) {
  return <span className={className} aria-hidden="true" dangerouslySetInnerHTML={{ __html: markup }} />;
}
import './PublicPage.css';

// Public, pre-auth surface for agentos.arkturian.com (Alex 2026-08-01: "wenn man
// neu ist gibts eine impressum datenschutz und onboarding page für neue user").
// Both hostnames serve the SAME build from the same document root, so these
// routes exist on cloud2 too — which is intentional: cloud2/privacy used to
// answer 200 with the login mask, and that was the actual defect.
//
// `/register` is canonical per AppDev's iOS contract (post #4426); `/onboarding`
// is an alias so Alex's wording works as a URL too.
const PUBLIC_ROUTES = ['/register', '/onboarding', '/privacy', '/impressum', '/support', '/product'] as const;
export type PublicRoute = (typeof PUBLIC_ROUTES)[number];

/** Returns the public route for a path, or null when the normal app should render. */
export function matchPublicRoute(pathname: string): PublicRoute | null {
  const p = pathname.replace(/\/+$/, '').toLowerCase() || '/';
  return (PUBLIC_ROUTES as readonly string[]).includes(p) ? (p as PublicRoute) : null;
}


export function PublicPage({ route }: { route: PublicRoute }) {
  const [current, setCurrent] = useState<PublicRoute>(route);
  // Die Produkt-/Anwerbe-Seite ist BETREIBER-Marketing. Auf einer Kunden-
  // instanz (standalone) wird sie weder verlinkt noch ausgeliefert — der
  // Kunde hat sein Produkt schon; Alex' Verkaufsseite gehört nicht hinein.
  const instanceMode = useInstanceMode();

  // Client-side navigation between the public pages; browser back/forward works.
  useEffect(() => {
    const onPop = () => {
      const next = matchPublicRoute(window.location.pathname);
      if (next) setCurrent(next);
      else window.location.reload(); // navigated back into the app itself
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((to: string) => (e: React.MouseEvent) => {
    const next = matchPublicRoute(to);
    if (!next) return; // "/" → let the browser load the app normally
    e.preventDefault();
    window.history.pushState({}, '', to);
    setCurrent(next);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pub">
      <header className="pub-head">
        <a className="pub-brand" href="/" >
          Arkturian Agent OS
        </a>
        <nav className="pub-nav">
          {instanceMode === 'federation' && (
            <a href="/product" onClick={go('/product')}>Produkt</a>
          )}
          <a href="/register" onClick={go('/register')}>Zugang</a>
          <a href="/support" onClick={go('/support')}>Support</a>
          <a href="/">Anmelden</a>
        </nav>
      </header>

      {current === '/product' ? (
        instanceMode === 'federation' ? <ProductPitch go={go} /> : <Onboarding />
      ) : current === '/register' || current === '/onboarding' ? (
        <Onboarding />
      ) : current === '/privacy' ? (
        <Privacy />
      ) : current === '/impressum' ? (
        <Impressum />
      ) : (
        <Support />
      )}

      <footer className="pub-foot">
        <div>
          <a href="/privacy" onClick={go('/privacy')}>Datenschutz</a>
          <a href="/impressum" onClick={go('/impressum')}>Impressum</a>
          <a href="/support" onClick={go('/support')}>Support</a>
        </div>
        <div>Arkturian Agent OS</div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * SaaS-Anwerbe-Seite (Alex „Go", 15.08.): AgentOS als verwaltetes Produkt —
 * eine eigene, isolierte Instanz je Kunde. Nur Zusagen, die das Design (#4701)
 * deckt; KEINE Preise (nicht entschieden), CTA führt auf die Support-Seite.
 * Wird nur im Betreiber-Modus verlinkt/gerendert (siehe PublicPage).
 */
function ProductPitch({ go }: { go: (to: string) => (e: React.MouseEvent) => void }) {
  return (
    <main className="pub-main">
      <p className="pub-eyebrow">AgentOS für Ihr Unternehmen</p>
      <h1>Ihre eigene KI-Agenten-Umgebung. Auf Ihrer Maschine. Nur für Sie.</h1>
      <p className="pub-lede">
        AgentOS betreibt ein Team von KI-Agenten als durchgängige Arbeitsumgebung —
        nicht als Chatfenster. Als Kunde erhalten Sie eine <strong>eigene, vollständig
        isolierte Instanz</strong>: Ihre Agenten, Ihre Daten, Ihre Maschine. Eingerichtet
        und betreut von uns.
      </p>

      <Art markup={heroFieldSvg} className="pub-hero-art" />

      <div className="pub-features">
        <div className="pub-feature">
          <Art markup={iconIsolation} className="pub-feature-art" />
          <h3>Isolation ist das Produkt</h3>
          <p>
            Ihre Instanz läuft auf Ihrer eigenen Hardware. Konversationen, Dokumente
            und Arbeitsstände verlassen Ihre Maschine nicht. Jede Instanz erhält
            eigene, frische Zugangs-Geheimnisse — nichts wird zwischen Kunden geteilt.
          </p>
        </div>
        <div className="pub-feature">
          <Art markup={iconTeam} className="pub-feature-art" />
          <h3>Ein Team, nicht ein Bot</h3>
          <p>
            Mehrere Agenten arbeiten parallel an Ihren Aufgaben, stimmen sich
            untereinander ab und geben Arbeit weiter. Sie sehen jederzeit, wer
            woran arbeitet, was fertig ist und wo es hakt.
          </p>
        </div>
        <div className="pub-feature">
          <Art markup={iconNachvollziehbar} className="pub-feature-art" />
          <h3>Nachvollziehbar statt Blackbox</h3>
          <p>
            Jeder Arbeitsschritt eines Agenten ist sichtbar — was er liest, was er
            ausführt, was er ändert. Die Erklärung obenauf, die Detailarbeit
            aufklappbar darunter.
          </p>
        </div>
        <div className="pub-feature">
          <Art markup={iconVerwaltet} className="pub-feature-art" />
          <h3>Verwaltet, nicht abgestellt</h3>
          <p>
            Wir richten Ihre Instanz ein, halten sie aktuell und überwachen ihre
            Gesundheit — ohne in Ihre Inhalte zu schauen: Zustandsdaten ja,
            Inhalte nie.
          </p>
        </div>
        <div className="pub-feature">
          <Art markup={iconPlattform} className="pub-feature-art" />
          <h3>Im Browser und als iOS-App</h3>
          <p>
            Volle Oberfläche im Browser, unterwegs per iPhone — inklusive Diktat
            und, je nach Ausstattung, Sprachsteuerung in Echtzeit.
          </p>
        </div>
        <div className="pub-feature">
          <Art markup={iconSkalierung} className="pub-feature-art" />
          <h3>Wächst mit</h3>
          <p>
            Vom einzelnen Agenten bis zum Verbund mehrerer Maschinen. Optionen wie
            Anhänge, Aufgaben-Boards und Sprachschicht werden je Vereinbarung
            freigeschaltet — Sie zahlen nur, was Sie nutzen.
          </p>
        </div>
      </div>

      <div className="pub-sales-cta">
        <h2>Interesse?</h2>
        <p>
          Preis und Ausstattung richten sich nach Umfang — vom Einzelplatz-Laptop
          bis zur Team-Installation. Schreiben Sie uns für eine Demo oder ein
          Angebot.
        </p>
        <a className="pub-cta-btn" href="/support" onClick={go('/support')}>
          Demo oder Angebot anfragen
        </a>
      </div>
    </main>
  );
}

function Onboarding() {
  // Standalone-Instanz: Die Landing beschreibt DIESE Instanz, nicht die
  // Betreiber-Föderation — Mehrmaschinen-Prosa wäre dort schlicht falsch (#4701).
  const instanceMode = useInstanceMode();
  return (
    <main className="pub-main">
      <p className="pub-eyebrow">Arkturian Agent OS</p>
      <h1>KI-Agenten betreiben, steuern und zusammenarbeiten lassen.</h1>
      <p className="pub-lede">
        {instanceMode === 'federation'
          ? 'Arkturian Agent OS führt KI-Agenten auf mehreren Rechnern zusammen — als durchgängige Arbeitsumgebung statt einzelner Chatfenster. Im Browser und als iOS-App.'
          : 'Arkturian Agent OS führt Ihre KI-Agenten in einer durchgängigen Arbeitsumgebung zusammen — statt einzelner Chatfenster. Im Browser und als iOS-App.'}
      </p>

      <div className="pub-features">
        <div className="pub-feature">
          <h3>{instanceMode === 'federation' ? 'Agenten auf mehreren Knoten' : 'Alle Agenten an einem Ort'}</h3>
          <p>
            {instanceMode === 'federation'
              ? 'Agenten laufen verteilt auf mehreren Maschinen und bleiben aus einer gemeinsamen Oberfläche erreichbar.'
              : 'Alle Agenten Ihrer Instanz laufen isoliert auf Ihrer eigenen Maschine und bleiben aus einer gemeinsamen Oberfläche erreichbar.'}
          </p>
        </div>
        <div className="pub-feature">
          <h3>Agenten reden miteinander</h3>
          <p>
            Sie stimmen sich direkt untereinander ab und geben Arbeit weiter, statt jede
            Frage über den Menschen zu leiten.
          </p>
        </div>
        <div className="pub-feature">
          <h3>Gemeinsame Dokumente</h3>
          <p>
            Mensch und Agenten schreiben in denselben Dokumenten — mit sichtbarer
            Urheberschaft, Fragen und Aufgaben an Ort und Stelle.
          </p>
        </div>
        <div className="pub-feature">
          <h3>Nachvollziehbare Arbeit</h3>
          <p>
            Aufgaben und Befunde werden zu durchsuchbaren Vorgängen mit Verlauf, statt in
            einem Chat zu verschwinden.
          </p>
        </div>
      </div>

      <h2>Zugang</h2>
      <p>
        Arkturian Agent OS wird derzeit nicht offen ausgerollt. Die Freischaltung erfolgt
        manuell, damit jeder Zugang zu einer Umgebung führt, die auch tatsächlich nutzbar
        ist.
      </p>

      <RegisterForm />
    </main>
  );
}

/* ------------------------------------------------------------------ */

interface SignupResponse {
  user?: { email?: string };
  verification?: { mail_sent?: boolean };
}

type FormState = { kind: 'form' } | { kind: 'sending' } | { kind: 'done'; mailSent: boolean; email: string };

function RegisterForm() {
  const [state, setState] = useState<FormState>({ kind: 'form' });
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Client-side throttle only. AuthAPI could not confirm a server-side rate limit
  // on /signup/*; this stops double-clicks and is explicitly NOT a substitute.
  const lastSubmit = useRef(0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.kind === 'sending') return;
    const now = Date.now();
    if (now - lastSubmit.current < 3000) return;
    lastSubmit.current = now;

    setError('');
    setState({ kind: 'sending' });
    try {
      // Contract read from auth-api's OpenAPI (EmailSignupRequest): email +
      // password required, names optional. Response shape confirmed by AuthAPI
      // against a real prod probe.
      const res = await fetch(`${serviceOrigin('auth')}/api/v1/auth/signup/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
        }),
      });

      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body?.detail || body?.message || '';
        } catch { /* non-JSON body */ }
        setError(
          res.status === 409
            ? 'Für diese Adresse existiert bereits ein Konto. Bitte melde dich an.'
            : detail || `Die Anfrage ist fehlgeschlagen (HTTP ${res.status}).`,
        );
        setState({ kind: 'form' });
        return;
      }

      const data: SignupResponse = await res.json();
      // mail_sent === false is a documented, tolerated outcome: the account exists
      // but no mail went out. "Check your inbox" would be a lie in that case.
      setState({
        kind: 'done',
        mailSent: data.verification?.mail_sent === true,
        email: data.user?.email || email.trim(),
      });
    } catch {
      setError('Der Server ist gerade nicht erreichbar. Bitte später erneut versuchen.');
      setState({ kind: 'form' });
    }
  };

  if (state.kind === 'done') {
    return (
      <>
        <h2>Deine Anfrage ist angekommen</h2>
        {state.mailSent ? (
          <p>
            Wir haben eine Bestätigungs-E-Mail an <strong>{state.email}</strong> geschickt.
            Bitte bestätige darin deine Adresse.
          </p>
        ) : (
          <div className="pub-error">
            Dein Konto wurde angelegt, aber die Bestätigungs-E-Mail konnte nicht
            zugestellt werden. Bitte wende dich an den Support, damit wir deine Adresse
            manuell bestätigen.
          </div>
        )}
        <div className="pub-notice">
          <p>
            <strong>Was jetzt passiert:</strong> Die Freischaltung für Arkturian Agent OS
            erfolgt manuell. Du bekommst Bescheid, sobald dein Zugang bereitsteht.
          </p>
          <p>Ein bestätigtes Konto allein schaltet noch keine Arbeitsumgebung frei.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Deliberately ABOVE the form: the contract (AppDev, post #4426) requires
          that the split between "account exists" and "AgentOS is usable" is
          understood BEFORE submitting, not discovered afterwards. */}
      <div className="pub-notice">
        <p>
          <strong>Was das Absenden bewirkt:</strong> Wir legen ein Konto an und senden
          eine Bestätigungs-E-Mail.
        </p>
        <p>
          <strong>Was es noch nicht bewirkt:</strong> Eine nutzbare Arbeitsumgebung
          entsteht dadurch nicht — die Freischaltung erfolgt danach manuell.
        </p>
        <p>
          Zur Verarbeitung deiner Daten siehe <a href="/privacy">Datenschutz</a>.
        </p>
      </div>

      {error && <div className="pub-error">{error}</div>}

      <form onSubmit={submit} noValidate>
        <div className="pub-row2">
          <div className="pub-field">
            <label htmlFor="pub-first">Vorname (optional)</label>
            <input id="pub-first" type="text" autoComplete="given-name" value={firstName}
              onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="pub-field">
            <label htmlFor="pub-last">Nachname (optional)</label>
            <input id="pub-last" type="text" autoComplete="family-name" value={lastName}
              onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div className="pub-field">
          <label htmlFor="pub-email">E-Mail-Adresse</label>
          <input id="pub-email" type="email" required autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="pub-field">
          <label htmlFor="pub-pw">Passwort</label>
          <input id="pub-pw" type="password" required minLength={8} autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <p className="pub-hint">Mindestens 8 Zeichen.</p>
        <button className="pub-btn" type="submit"
          disabled={state.kind === 'sending' || !email || !password}>
          {state.kind === 'sending' ? 'Wird gesendet…' : 'Zugang beantragen'}
        </button>
      </form>
    </>
  );
}

